import { useState, useEffect } from 'react';
import { WifiIcon } from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * OfflineBanner
 *
 * Appears at the top of the screen when the user loses internet connectivity.
 * Dismisses automatically when connectivity is restored.
 * The app continues to work for cached content via the service worker.
 */
export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);

  useEffect(() => {
    function handleOffline() { setIsOffline(true); }
    function handleOnline() { setIsOffline(false); }

    window.addEventListener('offline', handleOffline);
    window.addEventListener('online', handleOnline);

    return () => {
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('online', handleOnline);
    };
  }, []);

  return (
    <AnimatePresence>
      {isOffline && (
        <motion.div
          initial={{ y: -48, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -48, opacity: 0 }}
          transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] as const }}
          className="fixed top-0 left-0 right-0 z-50 flex items-center justify-center gap-2 px-4 py-2.5 bg-warning-600 text-white text-sm font-medium shadow-md"
          role="status"
          aria-live="polite"
        >
          <WifiIcon className="w-4 h-4 flex-shrink-0" />
          <span>You're offline — cached content is still available</span>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
